import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Maximize2,
  MessageSquare,
  Mic,
  MicOff,
  Minimize2,
  MonitorOff,
  MonitorUp,
  PhoneOff,
  Signal,
  Users,
  Video,
  VideoOff,
  X,
} from 'lucide-react';
import { useMeetingSessionOptional } from '../context/MeetingSessionContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import './meetings/LiveFloatingDock.css';

/* icons: Users / MessageSquare / Signal usados no dock */

function QualityBars({ quality }) {
  const level =
    quality === 'excellent' ? 4 : quality === 'good' ? 3 : quality === 'poor' ? 2 : quality === 'lost' ? 1 : 0;
  return (
    <span className={cn('lfd-bars', `is-${quality || 'unknown'}`)} aria-hidden>
      {[1, 2, 3, 4].map((i) => (
        <span key={i} className={cn('lfd-bars__seg', i <= level && 'is-on')} />
      ))}
    </span>
  );
}

/** Dock flutuante — live continua ao navegar no Office. */
export default function LiveFloatingDock() {
  const session = useMeetingSessionOptional();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const [pos, setPos] = useState({ x: null, y: null });
  const dragRef = useRef(null);

  const onRoomPage =
    location.pathname.startsWith('/reunioes/live/') || location.pathname.startsWith('/reunioes/sala/');
  const show = Boolean(session?.active && session?.minimized && !onRoomPage);

  useEffect(() => {
    if (!show || !session) return undefined;
    return session.registerFloatVideo(videoRef.current);
  }, [show, session, session?.stageKind, session?.sharing]);

  useEffect(() => {
    if (!show) return undefined;
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { ox, oy, sx, sy } = dragRef.current;
      setPos({
        x: Math.max(8, Math.min(window.innerWidth - 280, sx + (e.clientX - ox))),
        y: Math.max(8, Math.min(window.innerHeight - 160, sy + (e.clientY - oy))),
      });
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [show]);

  if (!show || !session) return null;

  const {
    meta,
    viewers,
    network,
    micOn,
    camOn,
    sharing,
    unreadChat,
    dockExpanded,
    setDockExpanded,
    toggleMic,
    toggleCam,
    toggleShare,
    disconnect,
    expand,
    path,
    stageKind,
  } = session;

  const style =
    pos.x != null ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined;

  const openFull = () => {
    expand();
    if (path) navigate(path);
  };

  const hangup = async () => {
    await disconnect();
  };

  return createPortal(
    <aside
      className={cn('lfd-dock', !dockExpanded && 'is-collapsed')}
      style={style}
      role="dialog"
      aria-label={t('meetings.room.floatTitle')}
    >
      <header
        className="lfd-dock__head"
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          const rect = e.currentTarget.parentElement.getBoundingClientRect();
          dragRef.current = { ox: e.clientX, oy: e.clientY, sx: rect.left, sy: rect.top };
        }}
      >
        <div className="lfd-dock__live">
          <span className="lfd-dock__pulse" />
          {meta?.mode === 'live' ? t('meetings.room.live') : t('meetings.room.title')}
        </div>
        <div className="lfd-dock__head-actions">
          <button
            type="button"
            className="lfd-icon-btn"
            onClick={() => setDockExpanded((v) => !v)}
            title={dockExpanded ? 'Recolher' : 'Expandir'}
          >
            {dockExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button type="button" className="lfd-icon-btn" onClick={openFull} title={t('meetings.room.floatExpand')}>
            <Maximize2 size={14} />
          </button>
          <button type="button" className="lfd-icon-btn is-danger" onClick={hangup} title={t('meetings.room.hangup')}>
            <X size={14} />
          </button>
        </div>
      </header>

      {dockExpanded ? (
        <>
          <div className="lfd-dock__video">
            <video ref={videoRef} playsInline autoPlay muted={stageKind === 'local-screen'} />
            {stageKind === 'idle' ? (
              <div className="lfd-dock__empty">
                <Users size={18} />
                <span>{t('meetings.room.waiting')}</span>
              </div>
            ) : null}
            <div className="lfd-dock__badges">
              <span>
                <Users size={11} /> {viewers.length}
              </span>
              {unreadChat ? (
                <span className="is-chat">
                  <MessageSquare size={11} /> {unreadChat}
                </span>
              ) : null}
              <span>
                <Signal size={11} />
                <QualityBars quality={network.quality} />
              </span>
            </div>
          </div>

          <div className="lfd-dock__stats">
            <span>{network.uplinkKbps || 0} ↑ kb/s</span>
            <span>{network.downlinkKbps || 0} ↓ kb/s</span>
            <span>{network.rttMs || 0} ms</span>
          </div>

          <div className="lfd-dock__controls">
            <Button type="button" size="icon" variant="outline" className={cn('lfd-ctrl', !micOn && 'is-off')} onClick={toggleMic}>
              {micOn ? <Mic size={14} /> : <MicOff size={14} />}
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              className={cn('lfd-ctrl', !camOn && 'is-off')}
              onClick={toggleCam}
              disabled={meta?.role === 'audience'}
            >
              {camOn ? <Video size={14} /> : <VideoOff size={14} />}
            </Button>
            {meta?.role !== 'audience' ? (
              <Button
                type="button"
                size="icon"
                variant="outline"
                className={cn('lfd-ctrl', sharing && 'is-active')}
                onClick={toggleShare}
              >
                {sharing ? <MonitorOff size={14} /> : <MonitorUp size={14} />}
              </Button>
            ) : null}
            <Button type="button" size="icon" className="lfd-ctrl is-hangup" onClick={hangup}>
              <PhoneOff size={14} />
            </Button>
            <Button type="button" size="sm" className="lfd-open" onClick={openFull}>
              {t('meetings.room.floatExpand')}
            </Button>
          </div>
        </>
      ) : (
        <button type="button" className="lfd-dock__pill" onClick={() => setDockExpanded(true)}>
          <span className="lfd-dock__pulse" />
          {viewers.length} · {network.quality || '—'}
          {unreadChat ? ` · ${unreadChat} msg` : ''}
        </button>
      )}
    </aside>,
    document.body,
  );
}
